import type {
  AssumptionRow,
  DedupeVerdict,
  GapAnalysis,
  Goal,
  Holding,
  InsightBundle,
  Sip,
  SubCategory,
  YearRow,
} from '@fire/core';
import { auth } from './firebase';

/**
 * Typed API client.
 *
 * Every request carries the current Firebase user's ID token as an
 * `Authorization: Bearer` header. There is nothing in localStorage and no
 * cookie to manage — `getIdToken()` reads Firebase's own in-memory token,
 * transparently refreshing it if it is close to expiry. When there is no
 * signed-in user the header is simply omitted, and the API answers 401.
 */
async function authHeader(): Promise<Record<string, string>> {
  const token = await auth.currentUser?.getIdToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Base URL for the API.
 *
 * Bearer-token auth (unlike the cookie it replaced) has no same-origin
 * requirement, so the API does not have to be proxied under this app's own
 * domain — it can be an absolute URL to wherever the Worker actually runs.
 * Local dev leaves this unset and keeps using Vite's `/api` proxy, which
 * still exists purely for convenience (one less thing to configure locally).
 */
const API_BASE = import.meta.env.VITE_API_URL ?? '/api';

export interface FieldError {
  path: string;
  message: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fields: FieldError[];
  readonly details: Record<string, unknown>;

  constructor(status: number, code: string, message: string, details: Record<string, unknown> = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.fields = Array.isArray(details.fields) ? (details.fields as FieldError[]) : [];
  }

  /** Message for a specific form field, if the server named one. */
  fieldError(path: string): string | undefined {
    return this.fields.find((f) => f.path === path)?.message;
  }

  get isAuthError(): boolean {
    return this.status === 401;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  // FormData must set its own Content-Type: the browser appends the multipart
  // boundary, and stamping application/json here makes the body unparseable
  // on the other end.
  const isFormData = typeof FormData !== 'undefined' && init.body instanceof FormData;
  if (init.body && !isFormData && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  for (const [key, value] of Object.entries(await authHeader())) headers.set(key, value);

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, { ...init, headers });
  } catch {
    // A network failure is not a zero — §9 forbids rendering a silent zero
    // where a fetch failed, so this has to surface as an error state.
    throw new ApiError(0, 'network_error', 'Could not reach the server. Check your connection.');
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = null;
    }
  }

  if (!response.ok) {
    const error = (body as { error?: { code?: string; message?: string; details?: Record<string, unknown> } })
      ?.error;
    throw new ApiError(
      response.status,
      error?.code ?? 'error',
      error?.message ?? 'Something went wrong.',
      error?.details ?? (body as Record<string, unknown>) ?? {},
    );
  }

  return body as T;
}

const get = <T>(path: string) => request<T>(path);
const post = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) });
const patch = <T>(path: string, body: unknown) =>
  request<T>(path, { method: 'PATCH', body: JSON.stringify(body) });
const del = <T>(path: string) => request<T>(path, { method: 'DELETE' });

// --- types mirrored from the API -------------------------------------------

export interface User {
  id: string;
  email: string;
  createdAt: number;
  baseCurrency: 'INR' | 'USD';
  riskProfile: 'Conservative' | 'Moderate' | 'Moderate-Aggressive' | 'Aggressive';
  fireTarget: number | null;
  fireYear: number | null;
  monthlyIncome: number | null;
  incomeGrowth: number;
  monthlyExpense: number | null;
  yearsWorking: number;
  termCover: number | null;
  healthCover: number | null;
  liabilities: number | null;
  metro: boolean;
  dependants: number;
  inflationPct: number;
  spendPct: number;
}

export interface AllocationSlice {
  key: string;
  value: number;
  pct: number;
}

export interface PortfolioSummary {
  netWorth: number;
  invested: number;
  unrealisedGain: number;
  unrealisedGainPct: number;
  blendedCagrCons: number;
  blendedCagrBest: number;
  totalMonthlySip: number;
  holdingCount: number;
  stalePriceCount: number;
  broad: AllocationSlice[];
  equity: AllocationSlice[];
  subCategories: AllocationSlice[];
}

export interface RowError {
  row: number;
  field: string;
  message: string;
}

export interface ImportPreview {
  ok: boolean;
  accepted: number;
  rejected: number;
  errors: RowError[];
  preview: Record<string, unknown>[];
  totals: { invested: number; currentValue: number };
  imported?: number;
}

export interface ProjectionResponse {
  startYear: number;
  horizonYears: number;
  /** Both scenarios always travel together — one line reads as a forecast. */
  cons: YearRow[];
  best: YearRow[];
  fireYearCons: number | null;
  fireYearBest: number | null;
  fireTarget: number | null;
  blendedCagrCons: number;
  blendedCagrBest: number;
  goals: { id: string; name: string; touchable: boolean; targetCorpus: number | null }[];
  incomplete: { income: boolean; expense: boolean };
  gapAnalysis: GapAnalysis;
}

export interface ExtractedRow {
  name: string;
  broadCat: string;
  subCat: SubCategory;
  identifier: string | null;
  identifierType: string | null;
  units: number | null;
  invested: number | null;
  currentValue: number | null;
  /** The model's own confidence it read the row correctly, 0–1. */
  confidence: number;
  /** Field names the document did not contain. */
  missing: string[];
}

export interface ExtractResponse {
  rows: ExtractedRow[];
  duplicates: DedupeVerdict[];
  /** What the model could not read, and rows it refused. Always surfaced. */
  notes: string[];
  error: string | null;
  quota?: { used: number; limit: number };
}

export interface NetWorthPoint {
  asOfDate: string;
  netWorth: number;
  invested: number;
  breakdown: Record<string, number>;
}

export interface NetWorthHistory {
  points: NetWorthPoint[];
  /** First day recorded, or null before any snapshot exists. */
  trackingSince: string | null;
  today: string;
}

export interface Installment {
  id: string;
  sipId: string;
  sipScheme: string;
  holdingName: string | null;
  dueDate: string;
  pricedDate: string | null;
  amount: number;
  nav: number | null;
  units: number | null;
  status: 'applied' | 'skipped';
  reason: string | null;
}

export interface PriceRefreshResult {
  attempted: number;
  updated: number;
  failed: number;
  /** Sources that could not be reached. Surfaced, never silently swallowed. */
  failures: { holdingId: string; reason: string }[];
}

export const api = {
  // --- auth ---
  //
  // Registration, login, logout and password reset happen entirely against
  // Firebase Authentication — see `lib/session.tsx` and `lib/firebase.ts`.
  // What is left here is our own side: reading and updating the profile row,
  // the strength check, and account deletion (our data only — see the
  // comment on the backend route about the Firebase account itself).
  me: () => get<{ user: User }>('/auth/me'),
  updateProfile: (patchBody: Partial<User>) => patch<{ user: User }>('/auth/profile', patchBody),
  passwordStrength: (password: string, email?: string) =>
    post<{
      score: number;
      acceptable: boolean;
      warning: string;
      suggestions: string[];
      minLength: number;
    }>('/auth/password-strength', email ? { password, email } : { password }),
  deleteAccount: () => del<{ ok: true }>('/auth/account'),

  // --- portfolio ---
  summary: () => get<{ summary: PortfolioSummary; user: User }>('/portfolio/summary'),

  // --- holdings ---
  holdings: () => get<{ holdings: Holding[] }>('/holdings'),
  createHolding: (body: Record<string, unknown>) => post<{ holding: Holding }>('/holdings', body),
  updateHolding: (id: string, body: Record<string, unknown>) =>
    patch<{ holding: Holding }>(`/holdings/${id}`, body),
  deleteHolding: (id: string) => del<{ ok: true }>(`/holdings/${id}`),
  importHoldings: (rows: unknown[], mode: 'append' | 'replace', dryRun: boolean) =>
    post<{ import: ImportPreview }>('/holdings/import', { rows, mode, dryRun }),

  /**
   * Send a statement — PDF, spreadsheet, CSV or screenshot — for extraction.
   * `FormData` sets its own multipart Content-Type with the boundary, so this
   * bypasses the JSON helper rather than fighting it.
   */
  extractHoldings: async (file: File): Promise<ExtractResponse> => {
    const body = new FormData();
    body.append('file', file);
    const { extract } = await request<{ extract: ExtractResponse }>('/holdings/extract', {
      method: 'POST',
      body,
    });
    return extract;
  },

  // --- goals and SIPs ---
  goals: () => get<{ goals: Goal[] }>('/goals'),
  createGoal: (body: Record<string, unknown>) => post<{ goal: Goal }>('/goals', body),
  updateGoal: (id: string, body: Record<string, unknown>) => patch<{ goal: Goal }>(`/goals/${id}`, body),
  deleteGoal: (id: string) => del<{ ok: true }>(`/goals/${id}`),

  sips: () => get<{ sips: Sip[] }>('/sips'),
  createSip: (body: Record<string, unknown>) => post<{ sip: Sip }>('/sips', body),
  updateSip: (id: string, body: Record<string, unknown>) => patch<{ sip: Sip }>(`/sips/${id}`, body),
  deleteSip: (id: string) => del<{ ok: true }>(`/sips/${id}`),

  // --- assumptions ---
  assumptions: () => get<{ assumptions: AssumptionRow[] }>('/assumptions'),
  setAssumption: (assetCat: SubCategory, consCagr: number, bestCagr: number) =>
    post<{ assumptions: AssumptionRow[] }>('/assumptions', { assetCat, consCagr, bestCagr }),
  clearAssumption: (assetCat: SubCategory) =>
    del<{ assumptions: AssumptionRow[] }>(`/assumptions/${encodeURIComponent(assetCat)}`),

  // --- projection and insights ---
  projection: (horizonYears: number) =>
    get<ProjectionResponse>(`/projection?horizonYears=${horizonYears}`),
  insights: (horizonYears: number) =>
    get<{ bundle: InsightBundle; source: 'deterministic' | 'ai'; disclaimer: string }>(
      `/insights?horizonYears=${horizonYears}`,
    ),
  aiInsights: (horizonYears: number, depth: 'standard' | 'deep') =>
    post<{
      bundle: InsightBundle;
      source: 'deterministic' | 'ai' | 'cache';
      quota: { used: number; limit: number };
      disclaimer: string;
    }>('/insights/generate', { horizonYears, depth }),

  // --- net worth history and SIP installments ---
  netWorthHistory: () => get<NetWorthHistory>('/networth/history'),
  recordSnapshot: () => post<{ snapshot: NetWorthPoint | null }>('/networth/snapshot'),
  installments: () => get<{ installments: Installment[] }>('/networth/installments'),

  // --- pricing ---
  refreshPrices: () => post<PriceRefreshResult>('/prices/refresh'),
  fxRate: () => get<{ usdInr: number | null; fetchedAt: number | null; stale: boolean }>('/prices/fx'),

  // --- config and export ---
  loanRates: () =>
    get<{ rates: { key: string; label: string; typicalPct: number; rangePct: [number, number] }[]; note: string; fetchedAt: number | null }>(
      '/config/loan-rates',
    ),
  exportUrl: (format: 'json' | 'csv') => `/api/export?format=${format}`,
};
